"use strict";
/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MockContainerRuntimeFactoryForReconnection = exports.MockContainerRuntimeForReconnection = void 0;
const uuid_1 = require("uuid");
const common_utils_1 = require("@fluidframework/common-utils");
const mocks_1 = require("./mocks");
/**
 * Specalized implementation of MockContainerRuntime for testing ops during reconnection.
 */
class MockContainerRuntimeForReconnection extends mocks_1.MockContainerRuntime {
    constructor(dataStoreRuntime, factory) {
        super(dataStoreRuntime, factory);
        /**
         * Contains messages from other clients that were sequenced while this runtime was marked as disconnected.
         */
        this.pendingRemoteMessages = [];
        this._connected = true;
    }
    get connected() {
        return this._connected;
    }
    set connected(connected) {
        if (this._connected === connected) {
            return;
        }
        this._connected = connected;
        if (connected) {
            for (const remoteMessage of this.pendingRemoteMessages) {
                this.process(remoteMessage);
            }
            this.pendingRemoteMessages.length = 0;
            this.clientSequenceNumber = 0;
            // We should get a new clientId on reconnection.
            this.clientId = (0, uuid_1.v4)();
            // Update the clientId in FluidDataStoreRuntime.
            this.dataStoreRuntime.clientId = this.clientId;
            this.factory.quorum.addMember(this.clientId, {});
            // On reconnection, ask the DDSes to resubmit pending messages.
            this.reSubmitMessages();
        }
        else {
            const factory = this.factory;
            // On disconnection, clear any outstanding messages for this client because it will be resent.
            factory.clearOutstandingClientMessages(this.clientId);
            this.factory.quorum.removeMember(this.clientId);
        }
        // Let the DDSes know that the connection state changed.
        this.deltaConnections.forEach((dc) => {
            dc.setConnectionState(this.connected);
        });
    }
    process(message) {
        if (this.connected) {
            super.process(message);
        }
        else {
            this.pendingRemoteMessages.push(message);
        }
    }
    submit(messageContent, localOpMetadata) {
        // Submit messages only if we are connection, otherwise, just add it to the pending queue.
        if (this.connected) {
            return super.submit(messageContent, localOpMetadata);
        }
        this.addPendingMessage(messageContent, localOpMetadata, -1);
        return -1;
    }
    reSubmitMessages() {
        let messageCount = this.pendingMessages.length;
        while (messageCount > 0) {
            const pendingMessage = this.pendingMessages.shift();
            (0, common_utils_1.assert)(pendingMessage !== undefined, "this is impossible due to the above length check");
            this.deltaConnections.forEach((dc) => {
                dc.reSubmit(pendingMessage.content, pendingMessage.localOpMetadata);
            });
            messageCount--;
        }
    }
}
exports.MockContainerRuntimeForReconnection = MockContainerRuntimeForReconnection;
/**
 * Specalized implementation of MockContainerRuntimeFactory for testing ops during reconnection.
 */
class MockContainerRuntimeFactoryForReconnection extends mocks_1.MockContainerRuntimeFactory {
    createContainerRuntime(dataStoreRuntime) {
        const containerRuntime = new MockContainerRuntimeForReconnection(dataStoreRuntime, this);
        this.runtimes.push(containerRuntime);
        return containerRuntime;
    }
    clearOutstandingClientMessages(clientId) {
        // Delete all the messages for client with the given clientId.
        this.messages = this.messages.filter((message) => {
            return message.clientId !== clientId;
        });
    }
}
exports.MockContainerRuntimeFactoryForReconnection = MockContainerRuntimeFactoryForReconnection;
//# sourceMappingURL=mocksForReconnection.js.map